/**
 * The credential and access-token contract.
 *
 * Three processes have to agree about what a login *is*, and none of them may
 * hold the only copy of the answer: `apps/api` mints tokens and stores accounts,
 * `apps/game` verifies a token in `onAuth` before it will seat anybody, and
 * `apps/web` collects the password and carries the token. This module is the one
 * definition all three read, for exactly the reason `messages.ts` is the one
 * definition of a message name — two sides that disagree here fail at runtime,
 * as an authentication error, which is the worst place to discover a typo.
 *
 * **Nothing here is a secret and nothing here is cryptography.** The signing key
 * comes from the environment of whichever process needs it and never from a
 * committed file; the signing itself is `jose`'s. What lives here is the shape:
 * which algorithm, which issuer, which audience, how long a token lives, and
 * what a well-formed email address and password look like before either reaches
 * a database or a key-derivation function.
 *
 * ## The algorithm is stated, never read
 *
 * `JWT_ALGORITHM` is what a verifier is *told to expect*. A verifier that takes
 * the algorithm from the token's own header is the algorithm-confusion hole: a
 * forged token can then declare `none`, or declare `HS256` against a service
 * that thought it was checking an RSA signature, and verify against nothing.
 * Both verifiers in this repo pass this constant as an allow-list.
 *
 * ## Refusals are results, not exceptions
 *
 * Every parser here takes `unknown` and hands back a result, because every one
 * of them is fed by a request body. A malformed email is an ordinary outcome of
 * a person mistyping, not a fault (`CLAUDE.md`).
 */
/** The one signing algorithm. Passed to the verifier as an allow-list, never read off a token. */
export const JWT_ALGORITHM = 'HS256';
/** Who minted the token. Only `apps/api` ever does. */
export const JWT_ISSUER = 'd2.api';
/**
 * Who the token is for. Deliberately not equal to the issuer: a service that
 * accepted its own issuer name as an audience would accept a token minted *for*
 * the issuer as one minted *by* it.
 */
export const JWT_AUDIENCE = 'd2.services';
/**
 * How long an access token lives, in seconds.
 *
 * Eight hours is one sitting. It is chosen against two failure modes rather
 * than out of the air: a token short enough to expire during a descent throws a
 * player out of a run they cannot rejoin, and a token long enough to be worth
 * stealing is a stolen character — and nothing in this milestone can revoke one
 * early, because there is no session table and no refresh token (`TODO.md`
 * #391). Until there is, the expiry *is* the whole revocation story, which is
 * the argument for the shorter end of "a sitting".
 */
export const ACCESS_TOKEN_TTL_SECONDS = 8 * 60 * 60;
/**
 * The longest address that will ever be stored.
 *
 * RFC 5321 §4.5.3.1.3 caps a forward path at 256 octets including the angle
 * brackets, which leaves 254 for the address itself. It is also the width of
 * the column.
 */
export const MAX_EMAIL_LENGTH = 254;
/**
 * Password length bounds, in code points.
 *
 * The minimum is stricter than NIST SP 800-63B's floor of 8 and short of its
 * "consider 15"; twelve is the compromise, and it is the *only* rule imposed.
 * There is no composition requirement on purpose — the same document has
 * recommended against them since 2017, because they push people towards
 * `Passw0rd!` and away from length.
 *
 * The maximum is not a security rule at all: argon2id over an unbounded input
 * is unbounded work on a request nobody has authenticated yet, so it is a
 * denial-of-service bound. 128 is comfortably above the 64 the same document
 * says must be accepted.
 */
export const MIN_PASSWORD_LENGTH = 12;
export const MAX_PASSWORD_LENGTH = 128;
/**
 * The key an account is stored and looked up under.
 *
 * Trimmed and lowercased **including the local part**, which is a deliberate
 * deviation from RFC 5321: the standard leaves the left of the `@`
 * case-sensitive, and no mailbox provider a player will actually use treats it
 * that way. Honouring the RFC here would let `Ronald@` and `ronald@` hold two
 * accounts on one mailbox, and then a password reset would have to ask which.
 *
 * Idempotent, which is what lets the same function key a row and key a lookup.
 */
export const normaliseEmail = (email) => email.trim().toLowerCase();
// Linear, with no nested quantifier that could be made to backtrack: the local
// part is one run of legal characters with single dots between, and the domain
// is labels of letters, digits and interior hyphens ending in a last label of
// two or more letters.
const LOCAL_PART = '[a-z0-9_%+-]+(?:\\.[a-z0-9_%+-]+)*';
const DOMAIN_LABEL = '[a-z0-9](?:[a-z0-9-]*[a-z0-9])?';
const EMAIL = new RegExp(`^${LOCAL_PART}@(?:${DOMAIN_LABEL}\\.)+[a-z]{2,}$`, 'u');
/**
 * An address fit to store, or the reason it is not.
 *
 * Deliberately narrower than RFC 5322 allows — no quoted local parts, no
 * bracketed IP literals, no comments. Every one of those is legal and none of
 * them is ever typed into a game's sign-up box, and the wider a parser is the
 * more shapes have to be safe everywhere the value later lands.
 *
 * The result carries the **normalised** address, so a caller cannot accidentally
 * store one spelling and look up another.
 */
export const parseEmail = (value) => {
    if (typeof value !== 'string')
        return { ok: false, reason: 'malformed' };
    const email = normaliseEmail(value);
    if (email.length > MAX_EMAIL_LENGTH)
        return { ok: false, reason: 'too-long' };
    if (!EMAIL.test(email))
        return { ok: false, reason: 'malformed' };
    return { ok: true, email };
};
/** C0 and C1 controls. Nothing here survives being typed into a password box. */
const CONTROL = /\p{Cc}/u;
/**
 * A password fit to hash, or the reason it is not.
 *
 * Handed back **exactly as typed** — not trimmed, not case-folded, not
 * normalised. Every one of those silently changes the secret, and a login that
 * edits the password before checking it is a login that will one day disagree
 * with the registration that stored it.
 *
 * Length is counted in code points rather than UTF-16 units, so an alphabet
 * outside the basic plane is not charged double.
 */
export const parsePassword = (value) => {
    if (typeof value !== 'string')
        return { ok: false, reason: 'malformed' };
    // A cheap bound before anything walks the string: a UTF-16 unit is never
    // fewer than half a code point, so twice the maximum in units is already too
    // many code points, whatever they are.
    if (value.length > MAX_PASSWORD_LENGTH * 2)
        return { ok: false, reason: 'too-long' };
    // `Array.from` rather than a spread: this counts *code points*, which is the
    // conventional unit for a password length, and is deliberately not the
    // grapheme count a display name is measured in — a family emoji is one
    // grapheme and seven code points, and a password is not being read aloud.
    const points = Array.from(value).length;
    if (points > MAX_PASSWORD_LENGTH)
        return { ok: false, reason: 'too-long' };
    if (points < MIN_PASSWORD_LENGTH)
        return { ok: false, reason: 'too-short' };
    if (CONTROL.test(value))
        return { ok: false, reason: 'malformed' };
    return { ok: true, password: value };
};
/**
 * Where `apps/game` asks `apps/api` for a character snapshot.
 *
 * Outside `/api/`, and that is the point rather than a naming preference:
 * `deploy/nginx/nl_ronaldswets_d2.conf` proxies **only** `/api/` to the API
 * container, so a path that does not start with it cannot be addressed from the
 * internet at all — a request for one lands on the web container. That is the
 * outermost of the three things guarding this route; the service token and the
 * proxy-header refusal in `apps/api` are the two the API enforces itself.
 */
export const INTERNAL_SNAPSHOT_PATH = '/internal/characters';
/**
 * The path for one character's snapshot.
 *
 * The id is percent-encoded, so an id carrying `/` or `..` becomes one segment
 * rather than a second one — the caller supplies an identifier, never a route.
 */
export const internalSnapshotPath = (characterId) => `${INTERNAL_SNAPSHOT_PATH}/${encodeURIComponent(characterId)}`;
/**
 * Where `apps/game` writes a character's containers back.
 *
 * The other half of the same conversation, under the same guarded prefix and
 * with the same two credentials: the service token says "this is the game
 * server", the player's own access token says which account is asking, and the
 * API derives ownership from the second. A game process that could write any
 * character by asserting an id would be a game process worth compromising.
 *
 * A separate path rather than a verb on the read one, because the two are
 * guarded differently the day anything asks them to be — and because a write is
 * the one thing in this system that can destroy a character's gear, so it is
 * worth being addressable, greppable and refusable on its own.
 *
 * The body is `CharacterStateUpdate` (`./characters.ts`), which is the same
 * vocabulary `CharacterSnapshot` came back in.
 */
export const internalStatePath = (characterId) => `${internalSnapshotPath(characterId)}/state`;
/** The header the game presents the service token in. */
export const SERVICE_TOKEN_HEADER = 'x-d2-service-token';
//# sourceMappingURL=auth.js.map